import React, { useEffect, useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { toast } from "react-toastify";
import { useCart } from "../context/CartContext";
import { AuthContext } from "../context/AuthContext";
import CarouselMulti from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import BestSellers from "../components/BestSellers";
import WishlistHeart from "../components/WishlistHeart";
import "../css/homepage.css";

// ─── Category definitions with Korean names ───────────────────────────────────
const STATIC_CATS = [
  { name: "Meat",       nameKr: "고기",   icon: "🥩" },
  { name: "Nuts",       nameKr: "견과류", icon: "🥜" },
  { name: "Dry Fruits", nameKr: "건과일", icon: "🍒" },
  { name: "Spices",     nameKr: "향신료", icon: "🌶️" },
  { name: "Ready Food", nameKr: "즉석식품", icon: "🍱" },
];

// ─── Features — horizontal bar like screenshot (labels come from locale files) ─
const FEATURES = [
  { icon: "🛡️", key: "halal"    },
  { icon: "🚚", key: "delivery" },
  { icon: "🔒", key: "payment"  },
  { icon: "🌿", key: "fresh"    },
];

// ─── Product Card ─────────────────────────────────────────────────────────────
const PCard = ({ product, imageUrl, onAdd }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const price    = product.is_on_sale ? product.sale_price : (product.sell_price || product.price || 0);
  const oldPrice = product.is_on_sale ? (product.compare_price || product.sell_price) : null;
  const discount = oldPrice && price < oldPrice ? Math.round(((oldPrice - price) / oldPrice) * 100) : null;
  const isOOS    = (product.stock_quantity ?? 1) === 0;
  const img      = imageUrl || product.imageUrl || "/images/default-product.jpg";

  return (
    <div className="pcard" onClick={() => navigate(`/product/${product._id}`)}>
      <div className="pcard-img-wrap">
        <img src={img} alt={product.name} className="pcard-img" loading="lazy" decoding="async"
          onError={(e) => { e.target.src = "/images/default-product.jpg"; }} />
        {discount > 0 && <span className="pcard-badge">-{discount}%</span>}
        <WishlistHeart productId={product._id} />
      </div>
      <div className="pcard-body">
        <div className="pcard-name">{product.name}</div>
        {product.weight && <div className="pcard-weight">{product.weight}</div>}
        <div className="pcard-footer">
          <div className="pcard-prices">
            <span className="pcard-price">₩{price.toLocaleString()}</span>
            {oldPrice && <span className="pcard-old">₩{oldPrice.toLocaleString()}</span>}
          </div>
          {isOOS
            ? <span className="pcard-oos">{t("product.outOfStock")}</span>
            : <button className="pcard-add-btn"
                onClick={(e) => { e.stopPropagation(); onAdd(product); }}
                aria-label="Add to cart">+</button>
          }
        </div>
      </div>
    </div>
  );
};

// ─── Main HomePage ────────────────────────────────────────────────────────────
function HomePage() {
  const navigate            = useNavigate();
  const { addToCart }       = useCart();
  const { isAuthenticated } = useContext(AuthContext);
  const { t }               = useTranslation();

  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [onSaleProducts,   setOnSaleProducts]   = useState([]);
  const [browseProducts,   setBrowseProducts]   = useState([]);
  const [categoryMap,      setCategoryMap]      = useState({}); // name → { id }

  // ─── One request to map category names → ids (for the category links) ─────
  useEffect(() => {
    api.get("/subcategories")
      .then(res => {
        const map = {};
        res.data.forEach(sub => {
          const cat = sub.category_id;
          if (!cat) return;
          if (!map[cat.name]) map[cat.name] = { id: cat._id };
        });
        setCategoryMap(map);
      })
      .catch(console.error);
  }, []);

  // ─── Products: paginated endpoints that include imageUrl in one query ─────
  useEffect(() => {
    api.get("/productsOP/featured?limit=12")
      .then(res => setFeaturedProducts(res.data?.products || []))
      .catch(console.error);

    api.get("/productsOP/all_products?page=1")
      .then(res => setBrowseProducts((res.data?.products || []).slice(0, 10)))
      .catch(console.error);

    api.get("/productsOP/highlighted?limit=24")
      .then(res => setOnSaleProducts((res.data?.products || []).filter(p => p.is_on_sale)))
      .catch(console.error);
  }, []);

  const handleAddToCart = async (product) => {
    const productId = product?._id || product?.product_id;
    if (!productId) { toast.error(t("product.invalidProduct")); return; }
    try {
      await addToCart(productId, 1);
      toast.success(t("product.addedToCartNamed", { name: product.name }), { position: "top-right", autoClose: 1500 });
    } catch {
      toast.error(t("product.addToCartFailed"), { position: "top-right", autoClose: 1500 });
    }
  };

  // Match static category name to real API category
  const getCatInfo = (name) => {
    const match = Object.entries(categoryMap).find(([catName]) =>
      catName.toLowerCase().includes(name.toLowerCase()) ||
      name.toLowerCase().includes(catName.toLowerCase())
    );
    return match ? { link: `/categories/${match[1].id}/products` } : { link: "/shop" };
  };

  const carouselResponsive = {
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 5 },
    tablet:  { breakpoint: { max: 1024, min: 768 },  items: 3 },
    mobile:  { breakpoint: { max: 768,  min: 0 },    items: 2 },
  };

  return (
    <div style={{ background: "#f9f7f4", fontFamily: "'DM Sans', sans-serif" }}>

      {/* ─── HERO ─────────────────────────────────────────────────────────── */}
      <section className="hero-section">
        <div className="hero-container">
          <div>
            <span className="hero-label"> {t("home.heroLabel")}</span>
            <h1 className="hero-title">
              {t("home.heroTitle1")}<br />
              <span>{t("home.heroTitle2")}</span>
            </h1>
            <p className="hero-subtitle">{t("home.heroSubtitle")}</p>
            <div className="hero-buttons">
              <button className="btn-green" onClick={() => navigate("/shop")}>{t("home.shopNow")}</button>
              <button className="btn-green-outline" onClick={() => navigate("/shop")}>{t("home.browseCategories")}</button>
            </div>
            <div className="hero-metrics">
              <div>
                <span className="hero-metric-value">12K+</span>
                <span className="hero-metric-label">{t("home.happyCustomers")}</span>
              </div>
              <div>
                <span className="hero-metric-value">100%</span>
                <span className="hero-metric-label">{t("home.halalVerified")}</span>
              </div>
              <div>
                <span className="hero-metric-value">24h</span>
                <span className="hero-metric-label">{t("home.seoulDelivery")}</span>
              </div>
            </div>
          </div>

          <div className="hero-image-wrap">
            <div className="hero-image-bg">
              <img
                src="/images/homepage.png"
                alt="Premium Halal Products"
                className="hero-product-img"
                onError={(e) => { e.target.src = "/images/default-product.jpg"; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─── FEATURES BAR — horizontal strip like screenshot ──────────────── */}
      <section className="features-section">
        <div className="section-wrap">
          <div className="features-grid">
            {FEATURES.map((f, i) => (
              <div className="feature-card" key={i}>
                <div className="feature-icon-wrap">{f.icon}</div>
                <div className="feature-text">
                  <div className="feature-title">{t(`home.features.${f.key}.title`)}</div>
                  <div className="feature-desc">{t(`home.features.${f.key}.desc`)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SHOP BY CATEGORY — matches screenshot exactly ────────────────── */}
      <section className="categories-section">
        <div className="section-wrap">
          <div className="categories-header">
            <div>
              <span className="section-label-tag">{t("home.categoriesTag")}</span>
              <h2 className="section-title-h2">{t("home.shopByCategory")}</h2>
            </div>
            <Link to="/shop" className="categories-view-all">{t("home.viewAllCategories")}</Link>
          </div>

          <div className="categories-grid">
            {STATIC_CATS.map((cat, i) => {
              const { link } = getCatInfo(cat.name);
              return (
                <Link key={i} to={link} className="cat-card">
                  <div className="cat-icon-circle">
                    <span>{cat.icon}</span>
                  </div>
                  <div className="cat-card-info">
                    <span className="cat-name">{cat.name}</span>
                    <span className="cat-name-kr">{cat.nameKr}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FEATURED PRODUCTS ────────────────────────────────────────────── */}
      {featuredProducts.length > 0 && (
        <section className="carousel-section">
          <div className="section-wrap">
            <span className="section-label-tag">{t("home.handpicked")}</span>
            <h2 className="section-title-h2" style={{ marginBottom: "32px" }}>{t("home.featuredProducts")}</h2>
            <CarouselMulti responsive={carouselResponsive}
              infinite autoPlay autoPlaySpeed={2500}
              arrows={false} showDots={false}
              draggable swipeable pauseOnHover itemClass="px-2">
              {featuredProducts.map(product => (
                <PCard key={product._id} product={product}
                  imageUrl={product.imageUrl} onAdd={handleAddToCart} />
              ))}
            </CarouselMulti>
          </div>
        </section>
      )}

      {/* ─── ON SALE ──────────────────────────────────────────────────────── */}
      {onSaleProducts.length > 0 && (
        <section className="carousel-section" style={{ background: "#fff" }}>
          <div className="section-wrap">
            <span className="section-label-tag">{t("home.specialDeals")}</span>
            <h2 className="section-title-h2" style={{ marginBottom: "32px" }}>{t("home.onSaleNow")}</h2>
            <CarouselMulti responsive={carouselResponsive}
              infinite autoPlay autoPlaySpeed={2800}
              arrows={false} showDots={false}
              draggable swipeable pauseOnHover itemClass="px-2">
              {onSaleProducts.map(product => (
                <PCard key={product._id} product={product}
                  imageUrl={product.imageUrl} onAdd={handleAddToCart} />
              ))}
            </CarouselMulti>
          </div>
        </section>
      )}

      {/* ─── BEST SELLERS (admin/plan controlled) ─────────────────────────── */}
      <BestSellers />

      {/* ─── BROWSE ALL PRODUCTS ──────────────────────────────────────────── */}
      <section className="products-browse-section">
        <div className="section-wrap">
          <span className="section-label-tag">{t("home.ourStore")}</span>
          <h2 className="section-title-h2" style={{ marginBottom: "32px" }}>{t("home.browseAllProducts")}</h2>
          <div className="browse-grid">
            {browseProducts.map(product => product?._id ? (
              <PCard key={product._id} product={product}
                imageUrl={product.imageUrl}
                onAdd={handleAddToCart} />
            ) : null)}
          </div>
          <div className="more-btn-wrap">
            <button className="btn-green" onClick={() => navigate("/shop")}>{t("home.viewAllProducts")}</button>
          </div>
        </div>
      </section>

    </div>
  );
}

export default HomePage;